import type { ReactNode } from "react"

import { getCohortTiming } from "components/mentorship/CohortExperience"

type CohortOverviewCardProps = {
  cohortName?: string | null
  startDate: Date | null
  endDate: Date | null
  memberCount?: number
  memberSummary?: string | null
  newSignalsCount?: number
  attendanceReviewCount?: number
  checklistCompletionPercent?: number | null
  selector?: ReactNode
  manageableCohortCount?: number
  mobileRelationshipRow?: ReactNode
}

const formatDate = (date?: Date | null) => date?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })

export function CohortOverviewCard({
  cohortName,
  startDate,
  endDate,
  memberCount = 0,
  memberSummary,
  newSignalsCount = 0,
  attendanceReviewCount = 0,
  checklistCompletionPercent = null,
  selector,
  manageableCohortCount = 0,
  mobileRelationshipRow,
}: CohortOverviewCardProps) {
  const timing = getCohortTiming(startDate, endDate)
  const metrics = [
    { label: "New safety signals", mobileLabel: "New signals", value: newSignalsCount, tone: newSignalsCount ? "text-[var(--cohort-clay)]" : "" },
    { label: "Attendance to review", mobileLabel: "Attendance to review", value: attendanceReviewCount, tone: "" },
    { label: "Checklist complete", mobileLabel: "Checklist done", value: checklistCompletionPercent === null ? "—" : `${checklistCompletionPercent}%`, tone: "" },
  ]
  const desktopMeta = [
    timing.label,
    startDate && endDate ? `${formatDate(startDate)} — ${formatDate(endDate)}` : null,
    memberSummary || `${memberCount} ${memberCount === 1 ? "member" : "members"}`,
  ].filter(Boolean).join(" · ")

  return (
    <header className="cohort-admin-overview cohort-mobile-bleed min-w-0">
      {selector ? (
        <div className="hidden items-center gap-4 border-b border-[var(--cohort-rule-soft)] pb-4 sm:flex">
          <span className="shrink-0 text-[13px] text-[var(--cohort-ink-muted)]">Managing</span>
          <div className="min-w-0 flex-1">{selector}</div>
          {manageableCohortCount > 1 ? <span className="shrink-0 text-sm text-[var(--cohort-ink-muted)]">{manageableCohortCount} cohorts you manage</span> : null}
        </div>
      ) : null}

      <div data-testid="cohort-admin-mobile-header" className="cohort-admin-mobile-header sm:hidden">
        {selector ? <div>{selector}</div> : <h1 className="text-[23px] leading-[1.2] tracking-[-0.01em]">{cohortName || "Selected cohort"}</h1>}
        <p className="cohort-admin-mobile-meta mt-1 flex items-center gap-3 text-sm text-[var(--cohort-ink-muted)]">
          <span>{timing.label}</span>
          <span className="h-3 w-px shrink-0 bg-[var(--cohort-rule)]" aria-hidden="true" />
          <span>{memberCount} {memberCount === 1 ? "person" : "people"}</span>
        </p>
      </div>

      {mobileRelationshipRow}

      <div data-testid="cohort-admin-summary" className="cohort-admin-summary border-b border-[var(--cohort-rule)] sm:flex sm:items-end sm:justify-between sm:gap-10 sm:py-[18px]">
        <div className="hidden min-w-0 sm:block">
          <h1 className="break-words text-[38px] leading-[1.1] tracking-[-0.02em] text-[var(--cohort-ink)]">{cohortName || "Selected cohort"}</h1>
          <p className="mt-2 text-[15px] text-[var(--cohort-ink-muted)]">{desktopMeta}</p>
        </div>
        <div data-testid="cohort-admin-metrics" className="cohort-admin-metrics grid grid-cols-3 sm:flex sm:items-end sm:gap-9">
          {metrics.map((metric, index) => (
            <div
              key={metric.label}
              data-testid={`cohort-admin-metric-${index}`}
              className={`min-w-0 py-3.5 ${
                index === 0
                  ? "pl-5 pr-3 sm:px-0"
                  : index === metrics.length - 1
                    ? "border-l border-[var(--cohort-rule-soft)] pl-3 pr-5 sm:border-0 sm:px-0"
                    : "border-l border-[var(--cohort-rule-soft)] px-3 sm:border-0 sm:px-0"
              }`}
            >
              <p className={`text-[13px] leading-[1.35] text-[var(--cohort-ink-muted)] ${metric.tone}`}><span className="sm:hidden">{metric.mobileLabel}</span><span className="hidden sm:inline">{metric.label}</span></p>
              <p className={`mt-1 text-[28px] leading-[1.1] sm:text-[34px] ${metric.tone}`}>{metric.value}</p>
            </div>
          ))}
        </div>
      </div>
    </header>
  )
}
